import React, {useEffect, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {Col, WideContent} from "@pg-design/grid-module";
import {mt, onDesktop, pb} from "@pg-design/helpers-css";
import {BrandSearchIcon, MapIcon} from "@pg-design/icons-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {useElementWasVisible, usePrevious} from "@pg-mono/hooks";
import {debounce, omit, range} from "@pg-mono/nodash";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IApiRange} from "@pg-mono/rp-api-routes";
import {LISTING_VENDOR_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../../app/rp_reducer";
import {ColumnList} from "../../../../../atoms/ColumnList";
import {ColumnListItem} from "../../../../../atoms/ColumnListItem";
import {InfoPanel} from "../../../../../atoms/InfoPanel";
import {Country} from "../../../../../region/types/Country";
import {useSearchForm} from "../../../../../search/hooks/use_search_form";
import {SearchFormValuesType} from "../../../../../search/types/SearchFormValuesType";
import {TdaPortal} from "../../../../../smart_ad_server/components/TdaPortal";
import {SmartAdServerPlacement} from "../../../../../smart_ad_server/SmartAdServerPlacement";
import {offersVisibleHit} from "../../../../../tracking/algolytics_hits/offers_visible_hit";
import {OfferBoxWithOfferDetailsAction} from "../../../../atoms/OfferBoxWithOfferDetailsAction";
import {BudgetFilterBox} from "../../../../components/BudgetFilterBox";
import {RoomsFilterBox} from "../../../../components/RoomsFilterBox";
import {SmartAdSASTypes} from "../../../../helpers/smart_add_sas_types";
import {useOffersViewsUrl} from "../../../../hooks/use_offers_views_url";
import {hitGTMOfferViewItemList} from "../../../../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../../../../tracking/offer_box_gtm_events";
import {IOfferListOffer} from "../../../../types/IOfferListOffer";
import {OfferType} from "../../../../types/OfferType";
import {ISelectedRegion} from "../../../actions/offer_list_selected_data_actions";
import {OFFER_LIST_PAGE_SIZE} from "../../../constants/offer_list";
import {isPromotionsListingQuery} from "../../../helpers/map_offer_list_promotions_query_param";
import {shouldShowOfferListingPromotionTabs} from "../../../helpers/should_show_offer_listing_promotion_tabs";
import {getCountryId} from "../../../helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {useGetOfferListingAllOffers} from "../../../hooks/use_get_offer_listing_all_offers";
import {disableDynamicPositionProps, getDynamicPositionsCssSelector, useListDynamicPositions} from "../../../hooks/use_list_dynamic_positions";
import {useOfferListingAmplitudeViewItemListTracking} from "../../../hooks/use_offer_listing_amplitude_view_item_list_tracking";
import {usePageLinkBuilder} from "../../../hooks/usePageLinkBuilder";
import {IOfferListListStore} from "../../../reducers/desktop_offer_list_list_reducer";
import {OfferListSubType} from "../../../types/OfferListSubType";
import {hasPriceFilter} from "../../../utils/has_price_filter";
import {DefaultOfferListingModal} from "../../DefaultOfferListingModal";
import {NextPageBox} from "../../NextPageBox";
import {OfferListingPromotionTabs} from "../../OfferListingPromotionTabs";
import {OfferListSort} from "../../OfferListSort";
import {GetHomeLinkingBox} from "../GetHomeLinkingBox";
import {NotificationsListBox} from "../NotificationsListBox";
import {OfferListHeader} from "../OfferListHeader";
import {OfferListMidBanner} from "../OfferListMidBanner";
import {PromotionsListingTile} from "../PromotionsListingTile";
import {ArchivedOffersListing} from "./ArchivedOffersListing";

interface IProps {
    list: IOfferListListStore;
    metaDataTitle: string;
    selectedRegion: ISelectedRegion[];
    offerListSubType?: OfferListSubType;
    customGridStyle?: {flex: string; maxWidth: string} | null;
    isInitialOfferListFetchDone: boolean;
}

export enum CreationListingPosition {
    SURPRISE_LONG = 17,
    SURPRISE_SHORT = 10
}

export const DefaultOfferListing = (props: IProps) => {
    const {list, metaDataTitle, selectedRegion, offerListSubType, customGridStyle, isInitialOfferListFetchDone} = props;
    const {
        count,
        offers,
        privileged_offers,
        page,
        sold_offers,
        sold_offers_count,
        sold_offers_page,
        requestState,
        latestQuery,
        page_size,
        allOffers,
        propertiesCount,
        listingContextPromotionalOffersCount,
        listingContextPromotionalOffersCountRequestState
    } = list;
    const searchFormValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const searchFormRooms = searchFormValues.rooms as IApiRange<number | "">;
    const priceFilterUsed = hasPriceFilter(searchFormValues);
    const allOffersCount = priceFilterUsed ? allOffers.filter((offer) => offer.has_required_prices === true).length : count;
    const buildPageLink = usePageLinkBuilder();
    const {isMobile} = useUserDevice();
    const location = useLocation();
    const routeQuery = parseSearch(location.search);
    const prevRequestState = usePrevious(requestState, RequestState.None);
    const offerType = !!latestQuery.type ? parseInt(latestQuery.type) : undefined;
    const currentCountry = getCountryId(latestQuery.country);
    const isAbroadListing = currentCountry && currentCountry !== Country.POLAND;
    const listingPageSize = page_size || OFFER_LIST_PAGE_SIZE;
    const lastPage = allOffersCount ? Math.ceil(allOffersCount / listingPageSize) : 1;
    const isPromotionsTab = isPromotionsListingQuery(routeQuery);
    const showPromotionTabs = shouldShowOfferListingPromotionTabs({
        latestQuery,
        routeQuery,
        listingContextPromotionalOffersCount,
        listingContextPromotionalOffersCountRequestState
    });
    const listingListKey = isPromotionsTab ? "promotions" : "all";

    const offerRequestReady = prevRequestState === RequestState.Waiting && requestState === RequestState.Success;
    const currentPage = sold_offers_count ? page + sold_offers_page - 1 : page;
    const isLastPage = page === lastPage;

    const {mainOffers, additionalOffers, mainOffersCount} = useGetOfferListingAllOffers({
        offersOnPage: offers,
        allOffers,
        latestQuery,
        searchFormValues,
        privilegedOffers: privileged_offers,
        isLastPage
    });

    const offersToRender = [...mainOffers, ...additionalOffers];

    const shouldShowRoomsFilterBox = mainOffers.length !== 0 && count >= 24 && searchFormRooms.upper === "" && searchFormRooms.lower === "";
    const shouldShowBudgetFilterBox = mainOffers.length !== 0 && count >= 24 && !searchFormValues.instalment && !searchFormValues.price.upper;

    const seenOffers = useRef<number[]>([]);
    const roomsFilterBoxPosition = 4;
    const budgetFilterBoxPosition = shouldShowRoomsFilterBox ? roomsFilterBoxPosition + 3 : roomsFilterBoxPosition;
    const priceDividerOfferIndex = offers.findIndex((el) => el.has_required_prices !== true) || 0;
    const dividedOffersCount = priceFilterUsed ? offers.slice(priceDividerOfferIndex, offers.length).length : 0;

    const {mapUrl} = useOffersViewsUrl();

    const debouncedOffersVisibleHit = debounce(() => {
        offersVisibleHit(seenOffers.current);

        seenOffers.current = [];
    }, 500);

    const offersToRenderIds = useMemo(() => [...offersToRender.map(({id}) => id.toString())], [offersToRender]);
    const offerToRenderIndexes = useMemo(() => {
        const indexes: {[key: string]: number} = {};
        offersToRender.forEach((offer, index) => {
            indexes[offer.id] = index;
        });
        return indexes;
    }, [offersToRender]);

    useElementWasVisible({
        ids: offersToRenderIds,
        indexes: offerToRenderIndexes,
        onElementVisible: (id, _, index) => {
            seenOffers.current = [...seenOffers.current, parseInt(id)];
            index !== undefined && offerBoxTracking.gtm.investTileView(parseInt(id), index);
            debouncedOffersVisibleHit();
        },
        once: true
    });

    const offersListLayoutKey = useMemo(
        () => `${listingListKey}|${location.search}|${offersToRender.map((offer, index) => `${offer.id}-${index}`).join(",")}`,
        [listingListKey, location.search, offersToRender]
    );

    const {wrapperProps, initializeDynamicPosition, getDynamicPositionProps} = useListDynamicPositions<HTMLDivElement>([offersListLayoutKey]);

    // Commented out due to RP-217 requirements
    // const midListingReels = useMemo(() => {
    //     return <ListingReels selectedRegions={props.selectedRegion} className="listing-reels" />;
    // }, [props.selectedRegion]);

    const {openLocationSearch} = useSearchForm();
    const handleHeaderClick = () => {
        openLocationSearch();
    };

    const prevMetaDataTitle = useRef<string | null>(null);

    const listingTitle = metaDataTitle.split(" - ")[0];

    useOfferListingAmplitudeViewItemListTracking({
        offers: offersToRender,
        listingTitle,
        routeQuery,
        latestQuery,
        requestState,
        isInitialOfferListFetchDone,
        isPromotionsTab,
        showPromotionTabs
    });

    const gtmMeta = {
        listId: "listing",
        listName: listingTitle,
        source: "listing" as const,
        tileEvents: {
            imageClick: offerBoxTracking.gtm.investTileImageClick,
            buttonClick: offerBoxTracking.gtm.investTileButtonClick,
            linkClick: offerBoxTracking.gtm.investTileLinkClick,
            logoClick: offerBoxTracking.gtm.investTileLogoClick
        }
    };

    useEffect(() => {
        if (requestState === RequestState.Success && prevMetaDataTitle.current !== metaDataTitle) {
            prevMetaDataTitle.current = metaDataTitle;
            hitGTMOfferViewItemList({
                offers: offersToRender,
                listId: gtmMeta.listId,
                listName: gtmMeta.listName
            });
        }
    }, [requestState, offersToRenderIds.join(","), metaDataTitle]);

    const renderListSection = useMemo(() => {
        let hasPriceDivider = false;

        const renderDivider = (has_required_prices: boolean | null) => {
            const shouldHaveDivider = !hasPriceDivider && has_required_prices === false;
            if (shouldHaveDivider) {
                hasPriceDivider = true;
            }
            return (
                shouldHaveDivider && (
                    <Col sm={4} md={12} lg={16} css={dividerStyles}>
                        <InfoPanel
                            icon={BrandSearchIcon}
                            heading="Znaleźliśmy także inne oferty, które mogą nie spełniać Twoich oczekiwań cenowych."
                            subHeading="Szczegółowych informacji udzielają deweloperzy"
                        />
                    </Col>
                )
            );
        };

        const getRegion = () => {
            const regionId = latestQuery.region && latestQuery.region?.length > 0 ? +latestQuery.region[latestQuery.region.length - 1] : undefined;
            const selectedRegionData = selectedRegion.find((region) => region.id === regionId);

            if (regionId && selectedRegion.length > 0) {
                return {
                    id: regionId,
                    name: selectedRegionData?.name,
                    slug: selectedRegionData?.slug,
                    parentSlug: selectedRegionData?.parent?.slug,
                    type: selectedRegionData?.type
                };
            }
            return {id: regionId, name: undefined, slug: undefined, parentSlug: undefined, type: undefined};
        };
        const region = getRegion();

        let suffix = 1;
        initializeDynamicPosition();

        return (
            <>
                {!isMobile && (!currentCountry || currentCountry === Country.POLAND) && (
                    <TdaPortal>
                        <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />
                    </TdaPortal>
                )}
                <DefaultOfferListingModal />

                <OfferListHeader
                    offerCount={allOffers.length > 0 ? mainOffersCount : count - dividedOffersCount}
                    propertiesCount={propertiesCount}
                    selectedRegion={selectedRegion}
                    latestQuery={latestQuery}
                    offerListSubType={offerListSubType}
                    onHeaderClick={handleHeaderClick}
                />

                {/*<WideContent css={[display("none"), onDesktop(display("block"), mb(4), w100)]}>*/}
                {/*    <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} fetchPriority="high" preventGtmOnMobile />*/}
                {/*</WideContent>*/}

                <WideContent>
                    <div css={offerListControlsStyle(showPromotionTabs)}>
                        {/* @deprecated Comment code releated with displaying "Mieszkania i domy" tab due to RP-217 requirements
                        this can be delted entirely in future if we will keep this approach */}
                        {/* <ListingTypeTabs
                            filters={searchFormValues}
                            offerType={offerType}
                            currentListingType={ListingType.OFFERS}
                            additionalParams={{sort: props.list.latestQuery.sort}}
                        /> */}

                        {showPromotionTabs && <OfferListingPromotionTabs />}

                        <div css={mapButtonsWrapperStyle}>
                            <Button css={mapButtonStyle} variant="filled_secondary" size="small" href={mapUrl} iconLeft={MapIcon}>
                                zobacz na mapie
                            </Button>

                            <OfferListSort type="select" name="offer-sort" latestQuerySort={latestQuery.sort} css={offerListSortWrapperStyle} />
                        </div>
                    </div>
                </WideContent>
                <ColumnList {...wrapperProps} css={listDynamicRowOrderStyle}>
                    {offersToRender.map((offer: IOfferListOffer, idx: number) => {
                        const position = idx + 1;
                        const isShortListCase = offersToRender.length < 3 && position === offersToRender.length; // list is short, so detect the last element
                        const isLastPage = page === lastPage;
                        const isFifthLastIndex = position === offersToRender.length - 5;
                        const isLastPosition = position === offersToRender.length;

                        const surpriseAdPosition =
                            offersToRender.length >= CreationListingPosition.SURPRISE_LONG
                                ? CreationListingPosition.SURPRISE_LONG
                                : CreationListingPosition.SURPRISE_SHORT;

                        return (
                            <React.Fragment key={`${offer.id}-${idx}`}>
                                {renderDivider(offer.has_required_prices)}
                                <ColumnListItem
                                    data-testid={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                    dynamicProps={getDynamicPositionProps()}
                                    customGridStyle={customGridStyle}
                                >
                                    <OfferBoxWithOfferDetailsAction
                                        offer={offer}
                                        latestQuery={
                                            offer.has_required_prices === false ? omit(latestQuery, ["price_0", "price_1", "instalment"]) : latestQuery
                                        }
                                        index={idx}
                                        gtmMeta={gtmMeta}
                                        trackAmplitudeSelectItem
                                        dataTestId={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
                                    />
                                </ColumnListItem>
                                {/*{position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} />}*/}
                                {/* Hidden due to RP-217 requirements */}
                                {/* {position === 2 && midListingReels} */}
                                {position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />}
                                {(isShortListCase || position === 3) && (
                                    <SmartAdServerPlacement adFormat={SmartAdSASTypes.OFFER_OF_THE_MONTH} customGridStyle={customGridStyle} />
                                )}
                                {(isShortListCase || position === 3 || position === 7 || position === 11) && (
                                    <SmartAdServerPlacement adFormat={SmartAdSASTypes.MONO_NATIVE_ADD} suffix={suffix++} customGridStyle={customGridStyle} />
                                )}

                                {isFifthLastIndex && <SmartAdServerPlacement adFormat={SmartAdSASTypes.IMAGE} customGridStyle={customGridStyle} />}
                                {position === surpriseAdPosition && (
                                    <SmartAdServerPlacement
                                        adFormat={SmartAdSASTypes.SURPRISE}
                                        referenceElementId={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                        customGridStyle={customGridStyle}
                                    />
                                )}
                                {position === 5 && <OfferListMidBanner />}

                                {position === 6 && (
                                    <PromotionsListingTile
                                        selectedRegion={selectedRegion}
                                        getDynamicPositionProps={getDynamicPositionProps}
                                        customGridStyle={customGridStyle}
                                        listingPage={page}
                                        tilePosition={position}
                                    />
                                )}

                                {isLastPosition && (
                                    <ColumnListItem {...getDynamicPositionProps()} customGridStyle={customGridStyle}>
                                        <NextPageBox
                                            isLastPage={isLastPage}
                                            offerTypes={offerType ? [offerType] : [OfferType.FLAT, OfferType.HOUSE]}
                                            nextPageLink={buildPageLink(page + 1)}
                                        />
                                    </ColumnListItem>
                                )}
                                {shouldShowRoomsFilterBox && position === roomsFilterBoxPosition && (
                                    <ColumnListItem
                                        dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}
                                        className="rp-filters-tile"
                                        customGridStyle={customGridStyle}
                                    >
                                        <RoomsFilterBox />
                                    </ColumnListItem>
                                )}
                                {shouldShowBudgetFilterBox && position === (isMobile ? budgetFilterBoxPosition - 1 : budgetFilterBoxPosition) && (
                                    <ColumnListItem
                                        dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}
                                        customGridStyle={customGridStyle}
                                    >
                                        <BudgetFilterBox />
                                    </ColumnListItem>
                                )}
                                {isLastPage &&
                                    isLastPosition && ( // only on last page, last position
                                        <>
                                            {region.name && (
                                                <ColumnListItem dynamicProps={getDynamicPositionProps()} customGridStyle={customGridStyle}>
                                                    <NotificationsListBox regionName={region.name} />
                                                </ColumnListItem>
                                            )}
                                            {offerType !== OfferType.COMMERCIAL && !isAbroadListing && (
                                                <ColumnListItem dynamicProps={getDynamicPositionProps()} customGridStyle={customGridStyle}>
                                                    <GetHomeLinkingBox offerType={offerType} region={region} />
                                                </ColumnListItem>
                                            )}
                                        </>
                                    )}
                            </React.Fragment>
                        );
                    })}
                </ColumnList>

                {sold_offers_count ? <ArchivedOffersListing offers={sold_offers} dividerVisible={currentPage === Math.ceil(allOffersCount / page_size)} /> : ""}
            </>
        );
    }, [offersToRenderIds, offerRequestReady, metaDataTitle, latestQuery, allOffers.length, listingListKey, location.search, showPromotionTabs]);

    return renderListSection;
};

//  Styles
const dividerStyles = css`
    padding: 3rem 0;
`;

const offerListControlsStyle = (hasPromotionTabs: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: ${hasPromotionTabs ? "space-between" : "flex-end"};
    ${pb(2)};
    ${mt(2)};

    ${onDesktop(css`
        ${pb(3)};
        ${mt(0)};
    `)};

    > div {
        ${pb(0)};
    }
`;

const mapButtonsWrapperStyle = css`
    display: flex;
    gap: 1rem;
    align-items: center;
`;

const mapButtonStyle = css`
    display: none;

    ${onDesktop(css`
        display: inherit;
    `)};
`;

const offerListSortWrapperStyle = css`
    display: none;
    padding-bottom: 0;

    ${onDesktop(css`
        display: block;
        flex: 0 0 16rem;
    `)};
`;

const listDynamicRowOrderStyle = css`
    @media (max-width: ${getThemeBreakpoint().md}) {
        ${getDynamicPositionsCssSelector(range(0, 2))} {
            order: -4;
        }

        .listing-reels {
            order: -3;
        }

        ${getDynamicPositionsCssSelector(range(2, 4))} {
            order: -2;
        }

        .rp-filters-tile {
            order: -1;
        }
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${getDynamicPositionsCssSelector(range(0, 6))} {
            order: -4;
        }

        .listing-reels {
            order: -3;
        }

        ${getDynamicPositionsCssSelector(range(6, 12))} {
            order: -2;
        }

        .listing-banner {
            order: -1;
        }
    }

    @media (min-width: ${getThemeBreakpoint().lg}) {
        ${getDynamicPositionsCssSelector(range(0, 8))} {
            order: -4;
        }
        ${getDynamicPositionsCssSelector(range(8, 16))} {
            order: -2;
        }
    }
`;
import React, {useEffect, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {Col, WideContent} from "@pg-design/grid-module";
import {mt, onDesktop, pb} from "@pg-design/helpers-css";
import {BrandSearchIcon, MapIcon} from "@pg-design/icons-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {useElementWasVisible, usePrevious} from "@pg-mono/hooks";
import {debounce, omit, range} from "@pg-mono/nodash";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IApiRange} from "@pg-mono/rp-api-routes";
import {LISTING_VENDOR_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../../app/rp_reducer";
import {ColumnList} from "../../../../../atoms/ColumnList";
import {ColumnListItem} from "../../../../../atoms/ColumnListItem";
import {InfoPanel} from "../../../../../atoms/InfoPanel";
import {Country} from "../../../../../region/types/Country";
import {useSearchForm} from "../../../../../search/hooks/use_search_form";
import {SearchFormValuesType} from "../../../../../search/types/SearchFormValuesType";
import {TdaPortal} from "../../../../../smart_ad_server/components/TdaPortal";
import {SmartAdServerPlacement} from "../../../../../smart_ad_server/SmartAdServerPlacement";
import {offersVisibleHit} from "../../../../../tracking/algolytics_hits/offers_visible_hit";
import {OfferBoxWithOfferDetailsAction} from "../../../../atoms/OfferBoxWithOfferDetailsAction";
import {BudgetFilterBox} from "../../../../components/BudgetFilterBox";
import {RoomsFilterBox} from "../../../../components/RoomsFilterBox";
import {SmartAdSASTypes} from "../../../../helpers/smart_add_sas_types";
import {useOffersViewsUrl} from "../../../../hooks/use_offers_views_url";
import {hitGTMOfferViewItemList} from "../../../../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../../../../tracking/offer_box_gtm_events";
import {IOfferListOffer} from "../../../../types/IOfferListOffer";
import {OfferType} from "../../../../types/OfferType";
import {ISelectedRegion} from "../../../actions/offer_list_selected_data_actions";
import {OFFER_LIST_PAGE_SIZE} from "../../../constants/offer_list";
import {isPromotionsListingQuery} from "../../../helpers/map_offer_list_promotions_query_param";
import {shouldShowOfferListingPromotionTabs} from "../../../helpers/should_show_offer_listing_promotion_tabs";
import {getCountryId} from "../../../helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {useGetOfferListingAllOffers} from "../../../hooks/use_get_offer_listing_all_offers";
import {disableDynamicPositionProps, getDynamicPositionsCssSelector, useListDynamicPositions} from "../../../hooks/use_list_dynamic_positions";
import {useOfferListingAmplitudeViewItemListTracking} from "../../../hooks/use_offer_listing_amplitude_view_item_list_tracking";
import {usePageLinkBuilder} from "../../../hooks/usePageLinkBuilder";
import {IOfferListListStore} from "../../../reducers/desktop_offer_list_list_reducer";
import {OfferListSubType} from "../../../types/OfferListSubType";
import {hasPriceFilter} from "../../../utils/has_price_filter";
import {DefaultOfferListingModal} from "../../DefaultOfferListingModal";
import {NextPageBox} from "../../NextPageBox";
import {OfferListingPromotionTabs} from "../../OfferListingPromotionTabs";
import {OfferListSort} from "../../OfferListSort";
import {GetHomeLinkingBox} from "../GetHomeLinkingBox";
import {NotificationsListBox} from "../NotificationsListBox";
import {OfferListHeader} from "../OfferListHeader";
import {OfferListMidBanner} from "../OfferListMidBanner";
import {PromotionsListingTile} from "../PromotionsListingTile";
import {ArchivedOffersListing} from "./ArchivedOffersListing";

interface IProps {
    list: IOfferListListStore;
    metaDataTitle: string;
    selectedRegion: ISelectedRegion[];
    offerListSubType?: OfferListSubType;
    customGridStyle?: {flex: string; maxWidth: string} | null;
    isInitialOfferListFetchDone: boolean;
}

export enum CreationListingPosition {
    SURPRISE_LONG = 17,
    SURPRISE_SHORT = 10
}

export const DefaultOfferListing = (props: IProps) => {
    const {list, metaDataTitle, selectedRegion, offerListSubType, customGridStyle, isInitialOfferListFetchDone} = props;
    const {
        count,
        offers,
        privileged_offers,
        page,
        sold_offers,
        sold_offers_count,
        sold_offers_page,
        requestState,
        latestQuery,
        page_size,
        allOffers,
        propertiesCount,
        listingContextPromotionalOffersCount,
        listingContextPromotionalOffersCountRequestState
    } = list;
    const searchFormValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const searchFormRooms = searchFormValues.rooms as IApiRange<number | "">;
    const priceFilterUsed = hasPriceFilter(searchFormValues);
    const allOffersCount = priceFilterUsed ? allOffers.filter((offer) => offer.has_required_prices === true).length : count;
    const buildPageLink = usePageLinkBuilder();
    const {isMobile} = useUserDevice();
    const location = useLocation();
    const routeQuery = parseSearch(location.search);
    const prevRequestState = usePrevious(requestState, RequestState.None);
    const offerType = !!latestQuery.type ? parseInt(latestQuery.type) : undefined;
    const currentCountry = getCountryId(latestQuery.country);
    const isAbroadListing = currentCountry && currentCountry !== Country.POLAND;
    const listingPageSize = page_size || OFFER_LIST_PAGE_SIZE;
    const lastPage = allOffersCount ? Math.ceil(allOffersCount / listingPageSize) : 1;
    const isPromotionsTab = isPromotionsListingQuery(routeQuery);
    const showPromotionTabs = shouldShowOfferListingPromotionTabs({
        latestQuery,
        routeQuery,
        listingContextPromotionalOffersCount,
        listingContextPromotionalOffersCountRequestState
    });
    const listingListKey = isPromotionsTab ? "promotions" : "all";

    const offerRequestReady = prevRequestState === RequestState.Waiting && requestState === RequestState.Success;
    const currentPage = sold_offers_count ? page + sold_offers_page - 1 : page;
    const isLastPage = page === lastPage;

    const {mainOffers, additionalOffers, mainOffersCount} = useGetOfferListingAllOffers({
        offersOnPage: offers,
        allOffers,
        latestQuery,
        searchFormValues,
        privilegedOffers: privileged_offers,
        isLastPage
    });

    const offersToRender = [...mainOffers, ...additionalOffers];

    const shouldShowRoomsFilterBox = mainOffers.length !== 0 && count >= 24 && searchFormRooms.upper === "" && searchFormRooms.lower === "";
    const shouldShowBudgetFilterBox = mainOffers.length !== 0 && count >= 24 && !searchFormValues.instalment && !searchFormValues.price.upper;

    const seenOffers = useRef<number[]>([]);
    const roomsFilterBoxPosition = 4;
    const budgetFilterBoxPosition = shouldShowRoomsFilterBox ? roomsFilterBoxPosition + 3 : roomsFilterBoxPosition;
    const priceDividerOfferIndex = offers.findIndex((el) => el.has_required_prices !== true) || 0;
    const dividedOffersCount = priceFilterUsed ? offers.slice(priceDividerOfferIndex, offers.length).length : 0;

    const {mapUrl} = useOffersViewsUrl();

    const debouncedOffersVisibleHit = debounce(() => {
        offersVisibleHit(seenOffers.current);

        seenOffers.current = [];
    }, 500);

    const offersToRenderIds = useMemo(() => [...offersToRender.map(({id}) => id.toString())], [offersToRender]);
    const offerToRenderIndexes = useMemo(() => {
        const indexes: {[key: string]: number} = {};
        offersToRender.forEach((offer, index) => {
            indexes[offer.id] = index;
        });
        return indexes;
    }, [offersToRender]);

    useElementWasVisible({
        ids: offersToRenderIds,
        indexes: offerToRenderIndexes,
        onElementVisible: (id, _, index) => {
            seenOffers.current = [...seenOffers.current, parseInt(id)];
            index !== undefined && offerBoxTracking.gtm.investTileView(parseInt(id), index);
            debouncedOffersVisibleHit();
        },
        once: true
    });

    const offersListLayoutKey = useMemo(
        () => `${listingListKey}|${location.search}|${offersToRender.map((offer, index) => `${offer.id}-${index}`).join(",")}`,
        [listingListKey, location.search, offersToRender]
    );

    const {wrapperProps, initializeDynamicPosition, getDynamicPositionProps} = useListDynamicPositions<HTMLDivElement>([offersListLayoutKey]);

    // Commented out due to RP-217 requirements
    // const midListingReels = useMemo(() => {
    //     return <ListingReels selectedRegions={props.selectedRegion} className="listing-reels" />;
    // }, [props.selectedRegion]);

    const {openLocationSearch} = useSearchForm();
    const handleHeaderClick = () => {
        openLocationSearch();
    };

    const prevMetaDataTitle = useRef<string | null>(null);

    const listingTitle = metaDataTitle.split(" - ")[0];

    useOfferListingAmplitudeViewItemListTracking({
        offers: offersToRender,
        listingTitle,
        routeQuery,
        latestQuery,
        requestState,
        isInitialOfferListFetchDone,
        isPromotionsTab,
        showPromotionTabs
    });

    const gtmMeta = {
        listId: "listing",
        listName: listingTitle,
        source: "listing" as const,
        tileEvents: {
            imageClick: offerBoxTracking.gtm.investTileImageClick,
            buttonClick: offerBoxTracking.gtm.investTileButtonClick,
            linkClick: offerBoxTracking.gtm.investTileLinkClick,
            logoClick: offerBoxTracking.gtm.investTileLogoClick
        }
    };

    useEffect(() => {
        if (requestState === RequestState.Success && prevMetaDataTitle.current !== metaDataTitle) {
            prevMetaDataTitle.current = metaDataTitle;
            hitGTMOfferViewItemList({
                offers: offersToRender,
                listId: gtmMeta.listId,
                listName: gtmMeta.listName
            });
        }
    }, [requestState, offersToRenderIds.join(","), metaDataTitle]);

    const renderListSection = useMemo(() => {
        let hasPriceDivider = false;

        const renderDivider = (has_required_prices: boolean | null) => {
            const shouldHaveDivider = !hasPriceDivider && has_required_prices === false;
            if (shouldHaveDivider) {
                hasPriceDivider = true;
            }
            return (
                shouldHaveDivider && (
                    <Col sm={4} md={12} lg={16} css={dividerStyles}>
                        <InfoPanel
                            icon={BrandSearchIcon}
                            heading="Znaleźliśmy także inne oferty, które mogą nie spełniać Twoich oczekiwań cenowych."
                            subHeading="Szczegółowych informacji udzielają deweloperzy"
                        />
                    </Col>
                )
            );
        };

        const getRegion = () => {
            const regionId = latestQuery.region && latestQuery.region?.length > 0 ? +latestQuery.region[latestQuery.region.length - 1] : undefined;
            const selectedRegionData = selectedRegion.find((region) => region.id === regionId);

            if (regionId && selectedRegion.length > 0) {
                return {
                    id: regionId,
                    name: selectedRegionData?.name,
                    slug: selectedRegionData?.slug,
                    parentSlug: selectedRegionData?.parent?.slug,
                    type: selectedRegionData?.type
                };
            }
            return {id: regionId, name: undefined, slug: undefined, parentSlug: undefined, type: undefined};
        };
        const region = getRegion();

        let suffix = 1;
        initializeDynamicPosition();

        return (
            <>
                {!isMobile && (!currentCountry || currentCountry === Country.POLAND) && (
                    <TdaPortal>
                        <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />
                    </TdaPortal>
                )}
                <DefaultOfferListingModal />

                <OfferListHeader
                    offerCount={allOffers.length > 0 ? mainOffersCount : count - dividedOffersCount}
                    propertiesCount={propertiesCount}
                    selectedRegion={selectedRegion}
                    latestQuery={latestQuery}
                    offerListSubType={offerListSubType}
                    onHeaderClick={handleHeaderClick}
                />

                {/*<WideContent css={[display("none"), onDesktop(display("block"), mb(4), w100)]}>*/}
                {/*    <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} fetchPriority="high" preventGtmOnMobile />*/}
                {/*</WideContent>*/}

                <WideContent>
                    <div css={offerListControlsStyle(showPromotionTabs)}>
                        {/* @deprecated Comment code releated with displaying "Mieszkania i domy" tab due to RP-217 requirements
                        this can be delted entirely in future if we will keep this approach */}
                        {/* <ListingTypeTabs
                            filters={searchFormValues}
                            offerType={offerType}
                            currentListingType={ListingType.OFFERS}
                            additionalParams={{sort: props.list.latestQuery.sort}}
                        /> */}

                        {showPromotionTabs && <OfferListingPromotionTabs />}

                        <div css={mapButtonsWrapperStyle}>
                            <Button css={mapButtonStyle} variant="filled_secondary" size="small" href={mapUrl} iconLeft={MapIcon}>
                                zobacz na mapie
                            </Button>

                            <OfferListSort type="select" name="offer-sort" latestQuerySort={latestQuery.sort} css={offerListSortWrapperStyle} />
                        </div>
                    </div>
                </WideContent>
                <ColumnList {...wrapperProps} css={listDynamicRowOrderStyle}>
                    {offersToRender.map((offer: IOfferListOffer, idx: number) => {
                        const position = idx + 1;
                        const isShortListCase = offersToRender.length < 3 && position === offersToRender.length; // list is short, so detect the last element
                        const isLastPage = page === lastPage;
                        const isFifthLastIndex = position === offersToRender.length - 5;
                        const isLastPosition = position === offersToRender.length;

                        const surpriseAdPosition =
                            offersToRender.length >= CreationListingPosition.SURPRISE_LONG
                                ? CreationListingPosition.SURPRISE_LONG
                                : CreationListingPosition.SURPRISE_SHORT;

                        return (
                            <React.Fragment key={`${offer.id}-${idx}`}>
                                {renderDivider(offer.has_required_prices)}
                                <ColumnListItem
                                    data-testid={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                    dynamicProps={getDynamicPositionProps()}
                                    customGridStyle={customGridStyle}
                                >
                                    <OfferBoxWithOfferDetailsAction
                                        offer={offer}
                                        latestQuery={
                                            offer.has_required_prices === false ? omit(latestQuery, ["price_0", "price_1", "instalment"]) : latestQuery
                                        }
                                        index={idx}
                                        gtmMeta={gtmMeta}
                                        trackAmplitudeSelectItem
                                        dataTestId={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
                                    />
                                </ColumnListItem>
                                {/*{position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} />}*/}
                                {/* Hidden due to RP-217 requirements */}
                                {/* {position === 2 && midListingReels} */}
                                {position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />}
                                {(isShortListCase || position === 3) && (
                                    <SmartAdServerPlacement adFormat={SmartAdSASTypes.OFFER_OF_THE_MONTH} customGridStyle={customGridStyle} />
                                )}
                                {(isShortListCase || position === 3 || position === 7 || position === 11) && (
                                    <SmartAdServerPlacement adFormat={SmartAdSASTypes.MONO_NATIVE_ADD} suffix={suffix++} customGridStyle={customGridStyle} />
                                )}

                                {isFifthLastIndex && <SmartAdServerPlacement adFormat={SmartAdSASTypes.IMAGE} customGridStyle={customGridStyle} />}
                                {position === surpriseAdPosition && (
                                    <SmartAdServerPlacement
                                        adFormat={SmartAdSASTypes.SURPRISE}
                                        referenceElementId={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                        customGridStyle={customGridStyle}
                                    />
                                )}
                                {position === 5 && <OfferListMidBanner />}

                                {position === 6 && (
                                    <PromotionsListingTile
                                        selectedRegion={selectedRegion}
                                        getDynamicPositionProps={getDynamicPositionProps}
                                        customGridStyle={customGridStyle}
                                        listingPage={page}
                                        tilePosition={position}
                                    />
                                )}

                                {isLastPosition && (
                                    <ColumnListItem {...getDynamicPositionProps()} customGridStyle={customGridStyle}>
                                        <NextPageBox
                                            isLastPage={isLastPage}
                                            offerTypes={offerType ? [offerType] : [OfferType.FLAT, OfferType.HOUSE]}
                                            nextPageLink={buildPageLink(page + 1)}
                                        />
                                    </ColumnListItem>
                                )}
                                {shouldShowRoomsFilterBox && position === roomsFilterBoxPosition && (
                                    <ColumnListItem
                                        dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}
                                        className="rp-filters-tile"
                                        customGridStyle={customGridStyle}
                                    >
                                        <RoomsFilterBox />
                                    </ColumnListItem>
                                )}
                                {shouldShowBudgetFilterBox && position === (isMobile ? budgetFilterBoxPosition - 1 : budgetFilterBoxPosition) && (
                                    <ColumnListItem
                                        dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}
                                        customGridStyle={customGridStyle}
                                    >
                                        <BudgetFilterBox />
                                    </ColumnListItem>
                                )}
                                {isLastPage &&
                                    isLastPosition && ( // only on last page, last position
                                        <>
                                            {region.name && (
                                                <ColumnListItem dynamicProps={getDynamicPositionProps()} customGridStyle={customGridStyle}>
                                                    <NotificationsListBox regionName={region.name} />
                                                </ColumnListItem>
                                            )}
                                            {offerType !== OfferType.COMMERCIAL && !isAbroadListing && (
                                                <ColumnListItem dynamicProps={getDynamicPositionProps()} customGridStyle={customGridStyle}>
                                                    <GetHomeLinkingBox offerType={offerType} region={region} />
                                                </ColumnListItem>
                                            )}
                                        </>
                                    )}
                            </React.Fragment>
                        );
                    })}
                </ColumnList>

                {sold_offers_count ? <ArchivedOffersListing offers={sold_offers} dividerVisible={currentPage === Math.ceil(allOffersCount / page_size)} /> : ""}
            </>
        );
    }, [offersToRenderIds, offerRequestReady, metaDataTitle, latestQuery, allOffers.length, listingListKey, location.search, showPromotionTabs]);

    return renderListSection;
};

//  Styles
const dividerStyles = css`
    padding: 3rem 0;
`;

const offerListControlsStyle = (hasPromotionTabs: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: ${hasPromotionTabs ? "space-between" : "flex-end"};
    ${pb(2)};
    ${mt(2)};

    ${onDesktop(css`
        ${pb(3)};
        ${mt(0)};
    `)};

    > div {
        ${pb(0)};
    }
`;

const mapButtonsWrapperStyle = css`
    display: flex;
    gap: 1rem;
    align-items: center;
`;

const mapButtonStyle = css`
    display: none;

    ${onDesktop(css`
        display: inherit;
    `)};
`;

const offerListSortWrapperStyle = css`
    display: none;
    padding-bottom: 0;

    ${onDesktop(css`
        display: block;
        flex: 0 0 16rem;
    `)};
`;

const listDynamicRowOrderStyle = css`
    @media (max-width: ${getThemeBreakpoint().md}) {
        ${getDynamicPositionsCssSelector(range(0, 2))} {
            order: -4;
        }

        .listing-reels {
            order: -3;
        }

        ${getDynamicPositionsCssSelector(range(2, 4))} {
            order: -2;
        }

        .rp-filters-tile {
            order: -1;
        }
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${getDynamicPositionsCssSelector(range(0, 6))} {
            order: -4;
        }

        .listing-reels {
            order: -3;
        }

        ${getDynamicPositionsCssSelector(range(6, 12))} {
            order: -2;
        }

        .listing-banner {
            order: -1;
        }
    }

    @media (min-width: ${getThemeBreakpoint().lg}) {
        ${getDynamicPositionsCssSelector(range(0, 8))} {
            order: -4;
        }
        ${getDynamicPositionsCssSelector(range(8, 16))} {
            order: -2;
        }
    }
`;
